/**
 * Payment Activity Feed
 *
 * Shows recent billing and payment events from activity_logs.
 * Displayed in /dashboard/wallet so agents have full visibility into
 * their payment history without leaving the wallet page.
 *
 * Events shown:
 * - payment_completed: Successful initial payment
 * - contract_renewed: Monthly subscription renewal
 * - payment_failed: Failed payment attempt
 * - subscription_updated: Subscription state changes
 * - subscription_deleted: Subscription terminated
 * - billing_portal_accessed: Agent visited billing portal
 */

import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, RefreshCw, AlertTriangle, CreditCard, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";

interface PaymentEvent {
  id: string;
  event_type: string;
  created_at: string;
  metadata: Record<string, unknown> | null;
}

const eventConfig: Record<string, {
  icon: typeof CheckCircle2;
  label: string;
  iconClass: string;
}> = {
  payment_completed: {
    icon: CheckCircle2,
    label: "Payment Received",
    iconClass: "text-primary",
  },
  contract_renewed: {
    icon: RefreshCw,
    label: "Subscription Renewed",
    iconClass: "text-primary",
  },
  payment_failed: {
    icon: XCircle,
    label: "Payment Failed",
    iconClass: "text-destructive",
  },
  subscription_updated: {
    icon: AlertTriangle,
    label: "Subscription Updated",
    iconClass: "text-accent-foreground",
  },
  subscription_deleted: {
    icon: XCircle,
    label: "Subscription Ended",
    iconClass: "text-muted-foreground",
  },
  billing_portal_accessed: {
    icon: CreditCard,
    label: "Billing Portal Visited",
    iconClass: "text-muted-foreground",
  },
};

const PAYMENT_EVENT_TYPES = [
  "payment_completed",
  "contract_renewed",
  "payment_failed",
  "subscription_updated",
  "subscription_deleted",
  "billing_portal_accessed",
];

interface PaymentActivityFeedProps {
  agentId: string;
  agentEmail: string;
}

export function PaymentActivityFeed({ agentId, agentEmail }: PaymentActivityFeedProps) {
  const [events, setEvents] = useState<PaymentEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!agentId && !agentEmail) {
      setLoading(false);
      return;
    }

    const fetchPaymentEvents = async () => {
      // Fetch payment-related activity logs for this agent
      // Match by actor_id OR actor_email to catch events logged before agent_id was set.
      const orParts: string[] = [];
      if (agentId) orParts.push(`actor_id.eq.${agentId}`);
      if (agentEmail) orParts.push(`actor_email.eq.${agentEmail}`);

      if (orParts.length === 0) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("activity_logs")
        .select("id, event_type, created_at, metadata")
        .in("event_type", PAYMENT_EVENT_TYPES)
        .or(orParts.join(","))
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) {
        console.error("[PaymentActivity] Failed to fetch:", error);
        setLoading(false);
        return;
      }

      setEvents((data as PaymentEvent[]) ?? []);
      setLoading(false);
    };

    fetchPaymentEvents();
  }, [agentId, agentEmail]);

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <h3 className="font-display text-base font-semibold text-foreground">Payment Activity</h3>
      </div>
      <div className="px-5 py-1">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No payment activity yet.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {events.map((event) => {
              const config = eventConfig[event.event_type] || {
                icon: CreditCard,
                label: event.event_type,
                iconClass: "text-muted-foreground",
              };
              const Icon = config.icon;
              const timeAgo = formatDistanceToNow(new Date(event.created_at), { addSuffix: true });

              return (
                <div key={event.id} className="flex items-center gap-3 py-3">
                  <div className="w-7 h-7 rounded-full bg-muted/50 flex items-center justify-center flex-shrink-0">
                    <Icon className={`w-3.5 h-3.5 ${config.iconClass}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{config.label}</p>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
